import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listUnitConversions, createUnitConversion, updateUnitConversion, deleteUnitConversion } from '@/app/services/inventoryService';
import { listUnits } from '@/app/services/unitService';
import { UnitConversion, UnitConversionCreate, UnitConversionUpdate } from '@/app/types/inventory';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Pencil, Plus, MoreHorizontal, Trash2, AlertTriangle } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import UnitConversionForm from './UnitConversionForm';

const UnitConversionManagement: React.FC = () => {
  const queryClient = useQueryClient();
  
  // State for managing dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // Selected conversion for edit/delete
  const [selectedConversion, setSelectedConversion] = useState<UnitConversion | null>(null);

  // Get conversions
  const {
    data: conversionsData,
    isLoading: isLoadingConversions,
    isError: isErrorConversions,
    error: conversionsError,
  } = useQuery({
    queryKey: ['unitConversions'],
    queryFn: () => listUnitConversions({}),
  });

  // Get units for form dropdowns - pass null for organizationId to include both global and org-specific units
  const {
    data: unitsData,
    isLoading: isLoadingUnits,
    isError: isErrorUnits,
  } = useQuery({
    queryKey: ['units'],
    queryFn: () => listUnits({}, 0), // Pass 0 or null to include all units
  });

  // Create conversion mutation
  const createConversionMutation = useMutation({
    mutationFn: (data: UnitConversionCreate) => createUnitConversion(data),
    onSuccess: () => {
      toast.success('Unit conversion created successfully');
      queryClient.invalidateQueries({ queryKey: ['unitConversions'] });
      setIsCreateDialogOpen(false);
    },
    onError: (error: Error) => {
      const errorDetail = (error as any).response?.data?.detail || 'An unknown error occurred';
      toast.error('Failed to create unit conversion', {
        description: errorDetail,
      });
    }
  });

  // Update conversion mutation
  const updateConversionMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UnitConversionUpdate }) => updateUnitConversion(id, data),
    onSuccess: () => {
      toast.success('Unit conversion updated successfully');
      queryClient.invalidateQueries({ queryKey: ['unitConversions'] });
      setIsEditDialogOpen(false);
      setSelectedConversion(null);
    },
    onError: (error: Error) => {
      const errorDetail = (error as any).response?.data?.detail || 'An unknown error occurred';
      toast.error('Failed to update unit conversion', {
        description: errorDetail,
      });
    }
  });

  // Delete conversion mutation
  const deleteConversionMutation = useMutation({
    mutationFn: (id: number) => deleteUnitConversion(id),
    onSuccess: () => {
      toast.success('Unit conversion deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['unitConversions'] });
      setIsDeleteDialogOpen(false);
      setSelectedConversion(null);
    },
    onError: (error: Error) => {
      const errorDetail = (error as any).response?.data?.detail || 'An unknown error occurred';
      toast.error('Failed to delete unit conversion', {
        description: errorDetail,
      });
    }
  });

  // Handle create conversion submit
  const handleCreateSubmit = (data: UnitConversionCreate | UnitConversionUpdate): void => {
    // In create mode, we know this is UnitConversionCreate
    createConversionMutation.mutate(data as UnitConversionCreate);
  };

  // Handle edit conversion submit
  const handleEditSubmit = (data: UnitConversionCreate | UnitConversionUpdate): void => {
    if (selectedConversion) {
      // In edit mode, we know this is UnitConversionUpdate
      updateConversionMutation.mutate({ 
        id: selectedConversion.id, 
        data: data as UnitConversionUpdate 
      });
    }
  };

  // Handle edit button click
  const handleEditClick = (conversion: UnitConversion): void => {
    setSelectedConversion(conversion);
    setIsEditDialogOpen(true);
  };

  // Handle delete button click
  const handleDeleteClick = (conversion: UnitConversion): void => {
    setSelectedConversion(conversion);
    setIsDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = (): void => {
    if (selectedConversion) {
      deleteConversionMutation.mutate(selectedConversion.id);
    }
  };

  // Show loading state
  if (isLoadingConversions || isLoadingUnits) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading unit conversions...</span>
      </div>
    );
  }

  // Show error state
  if (isErrorConversions) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to load unit conversions: {(conversionsError as Error)?.message || 'Unknown error'}
        </AlertDescription>
      </Alert>
    );
  }

  // Show error for units
  if (isErrorUnits) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to load units data needed for forms.
        </AlertDescription>
      </Alert>
    );
  }

  // Units are filtered by organization in the backend, so we just use the items directly
  const units = unitsData?.items || [];

  return (
    <div className="space-y-4">
      {/* Header with create button */}
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-muted-foreground">
            Manage conversion rules between different units of measurement
          </p>
        </div>
        <Button 
          onClick={() => setIsCreateDialogOpen(true)}
          className="flex items-center"
        >
          <Plus className="mr-2 h-4 w-4" /> Add New Conversion
        </Button>
      </div>

      {/* Conversions table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>From Unit</TableHead>
              <TableHead>To Unit</TableHead>
              <TableHead>Conversion Factor</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {conversionsData && conversionsData.items.length > 0 ? (
              conversionsData.items.map((conversion) => (
                <TableRow key={conversion.id}>
                  <TableCell className="font-medium">
                    {conversion.from_unit.name} ({conversion.from_unit.code})
                  </TableCell>
                  <TableCell>
                    {conversion.to_unit.name} ({conversion.to_unit.code})
                  </TableCell>
                  <TableCell>
                    1 {conversion.from_unit.name} = {conversion.conversion_factor} {conversion.to_unit.name}(s)
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => handleEditClick(conversion)}
                          className="cursor-pointer"
                        >
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDeleteClick(conversion)}
                          className="cursor-pointer text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                  No unit conversions found. Start by adding a new conversion rule.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Form Dialog */}
      <UnitConversionForm
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleCreateSubmit}
        isLoading={createConversionMutation.isPending}
        mode="create"
        units={units}
      />

      {/* Edit Form Dialog */}
      {selectedConversion && (
        <UnitConversionForm
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSubmit={handleEditSubmit}
          defaultValues={selectedConversion}
          isLoading={updateConversionMutation.isPending}
          mode="edit"
          units={units}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the unit conversion from 
              &quot;{selectedConversion?.from_unit.name}&quot; to 
              &quot;{selectedConversion?.to_unit.name}&quot;.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteConversionMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UnitConversionManagement; 